import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';

/** Purpose: render the responsive workspace navigation tree. Why: route access and navigation presentation are shared across desktop and mobile shells. */
export default function SidebarNav({
  items,
  collapsed,
  pathname,
  onNavigate,
  onToggleCollapse,
  footer,
  brand = 'ZelorichBot',
  expandedWidthClass = 'w-[270px]',
}) {
  const currentPath = pathname || '';
  const currentBasePath = currentPath.split('?')[0];
  const [openGroups, setOpenGroups] = useState({});

  /** Purpose: Find groups containing the active route recursively. Why: Parent navigation must stay active for nested destinations. */
  const hasActiveDescendant = (item) => {
    const children = Array.isArray(item?.children) ? item.children : [];
    return children.some((child) => isActivePath(child.path, child.exact) || hasActiveDescendant(child));
  };

  /** Purpose: Compare full and base paths with optional exact matching. Why: Query-specific pages and nested routes need distinct selection behavior. */
  const isActivePath = (path, exact = false) => {
    if (!path) return false;
    if (path.includes('?')) {
      return currentPath === path;
    }
    const basePath = path.split('?')[0];
    return currentPath === path || currentBasePath === basePath || (!exact && currentBasePath.startsWith(`${basePath}/`));
  };

  useEffect(() => {
    setOpenGroups((current) => {
      const next = { ...current };
      items.forEach((item) => {
        const children = Array.isArray(item.children) ? item.children : [];
        if (children.some((child) => isActivePath(child.path, child.exact) || hasActiveDescendant(child))) {
          next[item.key || item.text] = true;
          children.forEach((child) => { if (hasActiveDescendant(child)) next[child.key || child.text] = true; });
        }
      });
      return next;
    });
  }, [currentPath, items]);

  /** Purpose: Expand the sidebar if needed and toggle a navigation group. Why: Collapsed navigation must reveal children before selection. */
  const toggleGroup = (item) => {
    const groupKey = item.key || item.text;
    if (collapsed) {
      onToggleCollapse?.();
    }
    setOpenGroups((current) => ({ ...current, [groupKey]: !current[groupKey] }));
  };

  return (
    <aside
      className={[
        'workspace-nav flex h-full min-h-0 flex-col overflow-hidden border-r border-slate-200/80 bg-white/90 text-slate-900 backdrop-blur-xl transition-all duration-300 dark:border-white/10 dark:text-slate-100',
        collapsed ? 'w-[72px]' : expandedWidthClass,
      ].join(' ')}
    >
      <div className={`flex shrink-0 items-center ${collapsed ? 'justify-center flex-col gap-2' : 'justify-between gap-2'} border-b border-slate-200/80 px-3 py-2 dark:border-white/10`}>
        {collapsed ? (
          <>
            <img src="/favicon-32x32.png" alt="Zelorich" className="h-7 w-7 rounded-lg object-contain" />
            <motion.button
              type="button"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={onToggleCollapse}
              className="flex h-6 w-6 items-center justify-center rounded-lg border border-cyan-400/20 bg-gradient-to-br from-cyan-400 to-sky-500 text-xs font-bold text-slate-950 shadow-md transition dark:bg-none dark:bg-blue-500/10 dark:border-blue-400/20 dark:text-blue-200 dark:shadow-none"
              aria-label="Expand sidebar"
            >
              <span className="block text-base leading-none">›</span>
            </motion.button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2.5 min-w-0">
              <img src="/favicon-32x32.png" alt="Zelorich" className="h-7 w-7 shrink-0 rounded-lg object-contain" />
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500 dark:text-slate-500">Workspace</div>
                <div className="truncate text-base font-semibold leading-tight text-slate-900 dark:text-white">{brand}</div>
              </div>
            </div>
            <motion.button
              type="button"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.98 }}
              onClick={onToggleCollapse}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-cyan-400/20 bg-gradient-to-br from-cyan-400 to-sky-500 text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:shadow-cyan-500/30 dark:bg-none dark:bg-blue-500/10 dark:border-blue-400/20 dark:text-blue-200 dark:shadow-none"
              aria-label="Collapse sidebar"
            >
              <span className="block text-lg leading-none">‹</span>
            </motion.button>
          </>
        )}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col justify-between gap-1 overflow-y-auto overscroll-contain px-2 py-2 [scrollbar-gutter:stable]">
        {items.map((item, itemIndex) => {
          const children = Array.isArray(item.children) ? item.children : [];
          /** Purpose: Check a child route and its descendants for selection. Why: Nested navigation must highlight the current destination. */
          const childActive = children.some((child) => isActivePath(child.path, child.exact) || hasActiveDescendant(child));
          const active = isActivePath(item.path, item.exact) || childActive;
          const groupKey = item.key || item.text;
          const groupOpen = Boolean(openGroups[groupKey]);
          const showSection = !collapsed && item.section && (itemIndex === 0 || items[itemIndex - 1]?.section !== item.section);
          return (
            <div key={item.path || groupKey} className="shrink-0">
              {showSection ? (
                <div className="px-2.5 pb-0 pt-1 text-[8px] font-bold uppercase tracking-[0.14em] text-slate-400 first:pt-0 dark:text-slate-400">
                  {item.section}
                </div>
              ) : null}
            <div className="space-y-0.5">
              <button
                type="button"
                onClick={() => children.length > 0 ? toggleGroup(item) : onNavigate(item.path)}
                aria-expanded={children.length > 0 ? groupOpen : undefined}
                className={[
                  'group relative flex w-full items-center gap-2 overflow-hidden rounded-xl px-2.5 py-1 text-left transition-all duration-200',
                  active
                    ? 'bg-gradient-to-r from-slate-950 to-slate-800 text-white shadow-lg shadow-slate-900/10 dark:bg-none dark:bg-blue-500/10 dark:text-blue-100 dark:shadow-none dark:ring-1 dark:ring-blue-400/20'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white',
                ].join(' ')}
              >
                <span className={[
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border transition',
                  active ? 'border-cyan-400/20 bg-cyan-500/15 text-cyan-700 dark:text-cyan-200' : 'border-slate-200 bg-white text-slate-500 group-hover:border-slate-300 dark:border-white/5 dark:bg-white/5 dark:text-slate-300 dark:group-hover:border-white/10',
                ].join(' ')}>
                  {item.icon}
                </span>
                {!collapsed && (
                  <span className="flex min-w-0 flex-1 items-start gap-2">
                    <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-[12px] font-medium leading-[14px]">{item.text}</span>
                      {item.description ? (
                        <span className="truncate text-[9px] leading-3 text-slate-500 dark:text-slate-500">{item.description}</span>
                      ) : null}
                    </span>
                    {children.length > 0 && (
                      <ChevronRightIcon
                        fontSize="inherit"
                        className={[
                          'mt-0.5 text-base transition-transform',
                          groupOpen ? 'rotate-90 text-cyan-500' : 'text-slate-400',
                        ].join(' ')}
                      />
                    )}
                  </span>
                )}
              </button>

              <AnimatePresence initial={false}>
              {!collapsed && children.length > 0 && groupOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="ml-4 space-y-0.5 overflow-hidden border-l border-slate-200 pl-2 dark:border-white/10"
                >
                  {children.map((child) => {
                    const nestedChildren = Array.isArray(child.children) ? child.children : [];
                    const childIsActive = isActivePath(child.path, child.exact) || hasActiveDescendant(child);
                    const childKey = child.key || child.text;
                    const childOpen = Boolean(openGroups[childKey]);
                    if (nestedChildren.length) return (
                      <div key={childKey} className="space-y-0.5">
                        <button
                          type="button"
                          onClick={() => setOpenGroups((current) => ({ ...current, [childKey]: !current[childKey] }))}
                          aria-expanded={childOpen}
                          className={[
                            'flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left text-[11px] font-medium transition-all duration-200',
                            childIsActive ? 'bg-slate-100 text-slate-900 dark:bg-white/10 dark:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white',
                          ].join(' ')}
                        >
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-slate-500">{child.icon}</span>
                          <span className="min-w-0 flex-1 truncate">{child.text}</span>
                          <ChevronRightIcon fontSize="inherit" className={['text-sm transition-transform', childOpen ? 'rotate-90 text-cyan-500' : 'text-slate-400'].join(' ')} />
                        </button>
                        <AnimatePresence initial={false}>{childOpen ? <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="ml-3 space-y-0.5 overflow-hidden border-l border-slate-200 pl-1.5 dark:border-white/10">
                          {nestedChildren.map((nested) => {
                            const nestedActive = isActivePath(nested.path, nested.exact);
                            return <button key={nested.path} type="button" onClick={() => onNavigate(nested.path)} className={['flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left text-[10.5px] transition', nestedActive ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-200' : 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/5'].join(' ')}><span className="flex h-5 w-5 shrink-0 items-center justify-center">{nested.icon}</span><span className="truncate">{nested.text}</span></button>;
                          })}
                        </motion.div> : null}</AnimatePresence>
                      </div>
                    );
                    return (
                      <button
                        key={child.path}
                        type="button"
                        onClick={() => onNavigate(child.path)}
                        className={[
                          'flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left text-[11px] transition-all duration-200',
                          childIsActive
                            ? 'bg-cyan-500/10 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-200'
                            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-white',
                        ].join(' ')}
                      >
                        <span className={[
                          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition',
                          childIsActive ? 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-300' : 'bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-slate-500',
                        ].join(' ')}>
                          {child.icon || <span className="h-1.5 w-1.5 rounded-full bg-current" />}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{child.text}</span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
              </AnimatePresence>
            </div>
            </div>
          );
        })}
      </nav>

      {footer ? (
        <div className="border-t border-slate-200/80 p-3 dark:border-white/10">
          {footer}
        </div>
      ) : null}
    </aside>
  );
}
